import { useEffect, useState } from "react"

/**
 * Re-renders on an interval and returns an epoch ms that is at most
 * `intervalMs` old.
 *
 * This is NOT a countdown, and the distinction is the whole reason it exists.
 * The timer is shared and authoritative: every client derives remaining time
 * from `timer.startedAt` — a server timestamp — plus `durationMs`/`elapsedMs`,
 * so two screens agree to the second without talking to each other. A local
 * counter would drift, and worse, would keep counting after the facilitator
 * paused it on someone else's machine. So nothing here holds the remaining
 * time; it only supplies the renders in which that is recomputed.
 *
 * Pass `active: false` when there is nothing running — an idle timer does not
 * need a heartbeat.
 *
 * The value can be up to one interval stale, which is deliberately safe rather
 * than merely tolerated. `timerElapsedMs` reads `now` only while the timer is
 * running, so a stale value is ignored outright when it is idle or paused; and
 * for a timer that has just started, a value from before `startedAt` clamps to
 * zero elapsed. Both directions degrade to "shows the previous second", never to
 * a number ahead of the truth.
 */
export function useTick(active = true, intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (!active) return
    const id = window.setInterval(() => setNow(Date.now()), intervalMs)
    return () => window.clearInterval(id)
  }, [active, intervalMs])

  return now
}
